import { useCallback, useEffect, useRef } from 'react';

// Aligned with the lib/base SlideoutMenu Modal exit animation
// (`data-[exiting]:duration-500`).
const SIDEBAR_CLOSE_ANIMATION_MS = 500;

interface UseSidebarCloseAnimationReturn {
  /** Schedule the double-rAF visibility-flip open sequence. Cancels any
   *  pending close or open before scheduling the new one. */
  scheduleOpen: (cb: () => void) => void;
  /** Schedule the delayed close callback after the exit animation. Cancels
   *  any pending open rAF. No-ops if a close is already in flight. */
  scheduleClose: (cb: () => void) => void;
  /** Cancel any pending open rAF or close timeout. */
  cancelPending: () => void;
  /** Returns true while a close timeout is pending (i.e. mid-animation). */
  isClosePending: () => boolean;
}

/**
 * Owns the rAF and timeout refs that coordinate the proposal sidebar
 * open/close animation sequencing. Extracted from ProposalSidebarProvider
 * to keep the provider focused on state and stack management.
 */
export function useSidebarCloseAnimation(): UseSidebarCloseAnimationReturn {
  const closeTimeoutRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const openVisibilityRafRef = useRef<number | null>(null);

  useEffect(() => {
    return () => {
      if (closeTimeoutRef.current) {
        clearTimeout(closeTimeoutRef.current);
      }
      if (openVisibilityRafRef.current !== null) {
        cancelAnimationFrame(openVisibilityRafRef.current);
      }
    };
  }, []);

  const cancelPending = useCallback(() => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    if (openVisibilityRafRef.current !== null) {
      cancelAnimationFrame(openVisibilityRafRef.current);
      openVisibilityRafRef.current = null;
    }
  }, []);

  const scheduleOpen = useCallback((cb: () => void) => {
    if (closeTimeoutRef.current) {
      clearTimeout(closeTimeoutRef.current);
      closeTimeoutRef.current = null;
    }
    if (openVisibilityRafRef.current !== null) {
      cancelAnimationFrame(openVisibilityRafRef.current);
      openVisibilityRafRef.current = null;
    }
    openVisibilityRafRef.current = requestAnimationFrame(() => {
      openVisibilityRafRef.current = requestAnimationFrame(() => {
        openVisibilityRafRef.current = null;
        cb();
      });
    });
  }, []);

  const scheduleClose = useCallback((cb: () => void) => {
    if (openVisibilityRafRef.current !== null) {
      cancelAnimationFrame(openVisibilityRafRef.current);
      openVisibilityRafRef.current = null;
    }
    if (closeTimeoutRef.current) {
      // Already mid-close — don't restart the timer.
      return;
    }
    closeTimeoutRef.current = setTimeout(() => {
      closeTimeoutRef.current = null;
      cb();
    }, SIDEBAR_CLOSE_ANIMATION_MS);
  }, []);

  const isClosePending = useCallback(
    () => closeTimeoutRef.current !== null,
    []
  );

  return { scheduleOpen, scheduleClose, cancelPending, isClosePending };
}
